import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Printer, Download, ArrowLeft, Phone, Mail, MapPin, Globe, Home as HomeIcon, HardHat, Truck, Stethoscope, Smile, Scale, Calculator, Zap, Wrench, Snowflake, Briefcase, Mail as MailIcon, Headphones, FileText, Users, ShieldCheck, Sparkles, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";

const SLIDES = [
  { id: "cover", label: "Cover" },
  { id: "about", label: "About Us" },
  { id: "services", label: "Our Services" },
  { id: "industries", label: "Industries" },
  { id: "process", label: "Our Process" },
  { id: "why", label: "Why Choose Us" },
  { id: "security", label: "Security" },
  { id: "contact", label: "Contact" },
];

export default function CompanyProfile() {
  const [i, setI] = useState(0);
  const total = SLIDES.length;

  const go = useCallback((dir) => {
    setI((v) => Math.max(0, Math.min(total - 1, v + dir)));
  }, [total]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [go]);

  const print = () => window.print();
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/company-profile.pdf`;

  return (
    <main className="min-h-screen bg-[#050E1A] text-white profile-shell" data-testid="company-profile-page">
      {/* Screen-only chrome */}
      <div className="print:hidden">
        <div className="fixed top-4 inset-x-0 z-50 flex items-center justify-between px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white bg-white/5 border border-white/10 rounded-full px-4 py-2 backdrop-blur-xl" data-testid="cp-back-home">
            <ArrowLeft size={14} /> Back to site
          </Link>
          <div className="flex items-center gap-2">
            <div className="text-xs text-white/60 bg-white/5 border border-white/10 rounded-full px-3 py-2">
              Slide <span className="text-white font-semibold">{i + 1}</span> / {total}
            </div>
            <a
              href={pdfUrl}
              download="Admin-Apex-Company-Profile.pdf"
              data-testid="cp-download-pdf"
              className="inline-flex items-center gap-2 text-sm font-semibold bg-[#16A34A] hover:bg-[#15803D] text-white rounded-full px-4 py-2 transition"
            >
              <Download size={14} /> Download PDF
            </a>
            <button onClick={print} data-testid="cp-print" className="inline-flex items-center gap-2 text-sm font-semibold bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-4 py-2 transition">
              <Printer size={14} /> Print
            </button>
          </div>
        </div>

        <button
          onClick={() => go(-1)}
          disabled={i === 0}
          data-testid="cp-prev"
          className="fixed left-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 backdrop-blur-xl flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Previous"
        >
          <ChevronLeft />
        </button>
        <button
          onClick={() => go(1)}
          disabled={i === total - 1}
          data-testid="cp-next"
          className="fixed right-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 backdrop-blur-xl flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Next"
        >
          <ChevronRight />
        </button>

        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex gap-2 bg-white/5 border border-white/10 backdrop-blur-xl rounded-full px-3 py-2">
          {SLIDES.map((s, idx) => (
            <button
              key={s.id}
              onClick={() => setI(idx)}
              data-testid={`cp-dot-${idx}`}
              aria-label={s.label}
              className={`w-2 h-2 rounded-full transition-all ${idx === i ? "bg-[#16A34A] w-6" : "bg-white/30 hover:bg-white/60"}`}
            />
          ))}
        </div>
      </div>

      {/* Screen view: one slide at a time with transitions */}
      <div className="print:hidden">
        <AnimatePresence mode="wait">
          <motion.section
            key={SLIDES[i].id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="slide-frame"
          >
            <SlideBody id={SLIDES[i].id} pageNumber={i + 1} total={total} />
          </motion.section>
        </AnimatePresence>
      </div>

      {/* Print view: ALL slides stacked, one per page */}
      <div className="hidden print:block print-stack">
        {SLIDES.map((s, idx) => (
          <section key={s.id} className="slide-frame print-slide">
            <SlideBody id={s.id} pageNumber={idx + 1} total={total} />
          </section>
        ))}
      </div>

      <style>{`
        .slide-frame {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 6rem 2rem 5rem;
          background: linear-gradient(180deg, #050E1A 0%, #0B1B33 100%);
        }
        @media print {
          @page { size: A4 landscape; margin: 0; }
          html, body { background: #FFFFFF !important; color: #050E1A !important; }
          .profile-shell { background: #FFFFFF !important; color: #050E1A !important; }
          .slide-frame {
            page-break-after: always;
            break-after: page;
            min-height: 100vh;
            height: 100vh;
            padding: 3rem 3rem;
            background: #FFFFFF !important;
            color: #050E1A !important;
          }
          .slide-frame *:not(svg):not(svg *) { color: inherit !important; }
          .print-slide:last-child { page-break-after: auto; }
          .print-white-on-dark { background: #0B2545 !important; color: #FFFFFF !important; }
          .print-white-on-dark * { color: #FFFFFF !important; }
        }
      `}</style>
    </main>
  );
}

function SlideHeader({ children }) {
  return (
    <div className="inline-block text-[11px] tracking-[0.35em] font-bold text-[#16A34A] uppercase mb-4">
      {children}
    </div>
  );
}

function SlideBody({ id, pageNumber, total }) {
  const H = SlideHeader;

  switch (id) {
    case "cover":
      return (
        <div className="max-w-5xl w-full text-center print-white-on-dark p-16 rounded-3xl border border-white/10 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#16A34A]/25 blur-3xl pointer-events-none print:hidden" />
          <Logo size={64} withWordmark={false} className="justify-center mb-8" />
          <div className="text-[11px] tracking-[0.5em] text-[#16A34A] font-bold mb-6">COMPANY PROFILE · 2026</div>
          <h1 className="font-display font-black text-5xl md:text-7xl leading-[0.95] tracking-tighter">
            ADMIN <span className="text-[#16A34A]">APEX</span>
            <br />BUSINESS SOLUTIONS
          </h1>
          <div className="mt-6 text-lg text-white/70 max-w-2xl mx-auto">
            Your Complete Office Management Partner.
          </div>
          <div className="mt-14 pt-8 border-t border-white/10 grid grid-cols-3 gap-6 text-sm">
            <div>
              <div className="text-[10px] tracking-widest text-white/40 font-bold uppercase">Based in</div>
              <div className="mt-1 font-semibold">Brampton, Ontario</div>
            </div>
            <div>
              <div className="text-[10px] tracking-widest text-white/40 font-bold uppercase">Serving</div>
              <div className="mt-1 font-semibold">All of Canada</div>
            </div>
            <div>
              <div className="text-[10px] tracking-widest text-white/40 font-bold uppercase">Established</div>
              <div className="mt-1 font-semibold">2026</div>
            </div>
          </div>
        </div>
      );

    case "about":
      return (
        <div className="max-w-6xl w-full">
          <H>01 · About Us</H>
          <h2 className="font-display font-black text-4xl md:text-6xl leading-tight tracking-tighter">
            We become your <span className="text-[#16A34A]">office</span>.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-white/80 print:text-slate-700 leading-relaxed max-w-3xl">
            From customer service and email management to invoicing, document organization, and business administration — Admin Apex Business Solutions gives growing businesses a professional office team without the cost of hiring full-time staff.
          </p>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { label: "Our Mission", body: "To help businesses save time, reduce operational costs, and improve productivity through reliable office management solutions." },
              { label: "Our Approach", body: "We work as an extension of your business — not a vendor on a contract. Professional, confidential, efficient." },
              { label: "Who We Help", body: "Trucking companies, construction firms, medical clinics, law offices, real estate brokerages, and growing small businesses." },
            ].map((b) => (
              <div key={b.label} className="p-6 rounded-2xl border border-white/10 bg-white/5 print:border-slate-300 print:bg-slate-50">
                <div className="text-[10px] tracking-widest text-[#16A34A] font-bold uppercase">{b.label}</div>
                <div className="mt-3 text-sm leading-relaxed print:text-slate-700">{b.body}</div>
              </div>
            ))}
          </div>
        </div>
      );

    case "services": {
      const items = [
        { Icon: MailIcon, t: "Remote Office Administration", d: "Email, calendar, scheduling, document prep, data entry, digital filing, customer database." },
        { Icon: Headphones, t: "Customer Support", d: "Phone, email, live chat, appointment confirmations, follow-ups, complaint resolution." },
        { Icon: Calculator, t: "Bookkeeping Support", d: "Invoicing, AR/AP, expense tracking, QuickBooks entry, payment follow-up." },
        { Icon: Truck, t: "Trucking Office Administration", d: "Dispatch admin, driver files, safety docs, POD, fuel receipts, rate confirmations." },
        { Icon: Users, t: "HR & Payroll Administration", d: "Onboarding docs, timesheets, payroll prep, employee files, vacation tracking." },
        { Icon: FileText, t: "Document Management", d: "Physical & digital filing, scanning, secure cloud storage, records management." },
        { Icon: MapPin, t: "Weekly Office Support (GTA)", d: "On-site visits for paperwork, filing systems, employee files, admin projects." },
      ];
      return (
        <div className="max-w-7xl w-full">
          <H>02 · Our Services</H>
          <h2 className="font-display font-black text-4xl md:text-5xl leading-tight tracking-tighter">
            A complete back-office, <span className="text-[#16A34A]">modular by the hour.</span>
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((it, idx) => (
              <div key={it.t} className={`p-5 rounded-2xl border border-white/10 bg-white/5 print:border-slate-300 print:bg-white ${idx === items.length - 1 ? "lg:col-span-3 md:col-span-2" : ""}`}>
                <div className="w-10 h-10 rounded-lg bg-[#16A34A]/15 text-[#16A34A] flex items-center justify-center">
                  <it.Icon size={18} />
                </div>
                <div className="mt-4 font-display font-bold text-lg">{it.t}</div>
                <div className="mt-2 text-sm text-white/70 print:text-slate-600">{it.d}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    case "industries": {
      const rows = [
        { Icon: Truck, t: "Trucking & Transportation" },
        { Icon: HardHat, t: "Construction" },
        { Icon: Zap, t: "Electrical Contractors" },
        { Icon: Wrench, t: "Plumbing" },
        { Icon: Snowflake, t: "HVAC" },
        { Icon: HomeIcon, t: "Real Estate" },
        { Icon: Stethoscope, t: "Medical Clinics" },
        { Icon: Smile, t: "Dental Offices" },
        { Icon: Scale, t: "Law Firms" },
        { Icon: Calculator, t: "Accounting Firms" },
        { Icon: Briefcase, t: "Small Businesses" },
      ];
      return (
        <div className="max-w-6xl w-full">
          <H>03 · Industries We Serve</H>
          <h2 className="font-display font-black text-4xl md:text-5xl leading-tight tracking-tighter">
            Trusted by 11 industries <span className="text-[#16A34A]">across Canada</span>.
          </h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {rows.map((r) => (
              <div key={r.t} className="p-5 rounded-xl border border-white/10 bg-white/5 print:border-slate-300 print:bg-slate-50 flex flex-col gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#16A34A] text-white flex items-center justify-center">
                  <r.Icon size={18} />
                </div>
                <div className="font-display font-bold text-sm leading-tight">{r.t}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    case "process": {
      const steps = [
        ["01", "Free Consultation", "A no-pressure 30-minute call to understand your business and goals."],
        ["02", "Business Assessment", "We map your workflows, software stack, and admin work draining your team."],
        ["03", "Custom Proposal", "Written proposal — scope, hours, deliverables, security & confidentiality."],
        ["04", "Secure Onboarding", "NDAs signed, secure access set up, processes documented, team briefed."],
        ["05", "Daily Support", "Your dedicated team gets to work — reliable, organized, responsive."],
        ["06", "Monthly Review", "We meet monthly to review KPIs and scale support as you grow."],
      ];
      return (
        <div className="max-w-6xl w-full">
          <H>04 · Our Process</H>
          <h2 className="font-display font-black text-4xl md:text-5xl leading-tight tracking-tighter">
            Six steps to a <span className="text-[#16A34A]">quieter inbox</span>.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {steps.map(([n, t, d]) => (
              <div key={n} className="p-5 rounded-2xl border border-white/10 bg-white/5 print:border-slate-300 print:bg-white flex gap-4">
                <div className="w-12 h-12 shrink-0 rounded-xl bg-[#16A34A] text-white flex items-center justify-center font-display font-black">
                  {n}
                </div>
                <div>
                  <div className="font-display font-bold text-lg">{t}</div>
                  <div className="text-sm text-white/70 print:text-slate-600 mt-1">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    case "why": {
      const items = [
        { Icon: MapPin, t: "Canadian Business Support", d: "Built in Brampton — we understand Canadian operations and timezones." },
        { Icon: Lock, t: "Confidential & Secure", d: "NDAs available. Strict confidentiality practices for every engagement." },
        { Icon: Sparkles, t: "Flexible Monthly Plans", d: "Few hours per week or a full outsourced team — scale with your workload." },
        { Icon: HomeIcon, t: "Remote & On-Site Support", d: "Work with us remotely, or book weekly on-site visits across the GTA." },
        { Icon: ShieldCheck, t: "Fast Response Times", d: "Responsive, reliable, and consistent — week after week." },
        { Icon: Users, t: "Customized Business Solutions", d: "No cookie-cutter packages. Plans shaped around your workflow." },
      ];
      return (
        <div className="max-w-6xl w-full">
          <H>05 · Why Choose Admin Apex</H>
          <h2 className="font-display font-black text-4xl md:text-5xl leading-tight tracking-tighter">
            Built around the way you <span className="text-[#16A34A]">actually run a business</span>.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((it) => (
              <div key={it.t} className="p-5 rounded-2xl border border-white/10 bg-white/5 print:border-slate-300 print:bg-white">
                <div className="w-10 h-10 rounded-lg bg-[#0B2545] text-white flex items-center justify-center">
                  <it.Icon size={18} />
                </div>
                <div className="mt-4 font-display font-bold text-base">{it.t}</div>
                <div className="mt-2 text-sm text-white/70 print:text-slate-600">{it.d}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    case "security":
      return (
        <div className="max-w-5xl w-full">
          <H>06 · Security & Confidentiality</H>
          <h2 className="font-display font-black text-4xl md:text-6xl leading-tight tracking-tighter">
            Your information is <span className="text-[#16A34A]">safe</span>.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-white/80 print:text-slate-700 leading-relaxed max-w-3xl">
            Admin Apex Business Solutions follows strict confidentiality practices. We can work under Non-Disclosure Agreements (NDAs) and handle your business information with professionalism and discretion.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              ["NDAs on request", "Mutual non-disclosure agreements signed before any project starts."],
              ["Secure access", "Password managers, role-based logins, least-privilege access."],
              ["Confidential by default", "Client data is never shared, sold, or used elsewhere."],
            ].map(([t, d]) => (
              <div key={t} className="p-6 rounded-2xl border border-[#16A34A]/40 bg-[#16A34A]/5 print:bg-emerald-50">
                <ShieldCheck className="text-[#16A34A]" />
                <div className="mt-4 font-display font-bold">{t}</div>
                <div className="mt-2 text-sm text-white/70 print:text-slate-700">{d}</div>
              </div>
            ))}
          </div>
        </div>
      );

    case "contact":
      return (
        <div className="max-w-5xl w-full text-center print-white-on-dark p-16 rounded-3xl border border-white/10 relative overflow-hidden">
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#16A34A]/25 blur-3xl print:hidden" />
          <H>07 · Get In Touch</H>
          <h2 className="font-display font-black text-4xl md:text-6xl leading-tight tracking-tighter">
            Ready to <span className="text-[#16A34A]">simplify your office?</span>
          </h2>
          <p className="mt-6 text-lg text-white/70 max-w-2xl mx-auto">
            Book a FREE 30-minute consultation. We&apos;ll learn your business and outline a tailored support plan.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-left">
            <ContactCard Icon={Phone} label="Phone" value="+1 (647) 510-7379" />
            <ContactCard Icon={Mail} label="Email" value="info@adminapex.ca" />
            <ContactCard Icon={Globe} label="Web" value="www.adminapex.ca" />
            <ContactCard Icon={MapPin} label="Location" value="Brampton, ON" />
          </div>
          <div className="mt-10 text-xs tracking-widest text-white/40 font-bold uppercase">
            © 2026 Admin Apex Business Solutions · All rights reserved
          </div>
        </div>
      );

    default:
      return null;
  }
}

function ContactCard({ Icon, label, value }) {
  return (
    <div className="p-5 rounded-2xl border border-white/10 bg-white/5 print:border-slate-300 print:bg-slate-50">
      <div className="w-10 h-10 rounded-lg bg-[#16A34A]/15 text-[#16A34A] flex items-center justify-center">
        <Icon size={18} />
      </div>
      <div className="mt-3 text-[10px] tracking-widest text-white/50 print:text-slate-500 font-bold uppercase">{label}</div>
      <div className="mt-1 font-display font-bold text-sm">{value}</div>
    </div>
  );
}
